/**
 * Flattens object so that nested objects are represented as keys in the top object.
 * Recursively flattens child objects/arrays.
 *
 * E.g. `{ "key1": "aaa", "key2": ["bbb", "ccc"], "key3": { "key4": 1 } }`
 * results in `{ "key1": "aaa", "key2.0": "bbb", "key2.1": "ccc", "key3.key4": 1 }`
 */
export function flattenObject(obj: object, separator: string = '.') {
    const flat: Record<string, unknown> = {};

    Object.entries(obj).forEach(([key, value]) => {
        // If value is an object and not null (null is considered an object...)
        if (value && typeof value === 'object') {
            // Recursively flatten child object
            const flattenedChild = Array.isArray(value)
                ? // eslint-disable-next-line @typescript-eslint/no-use-before-define
                  flattenArray(value, separator)
                : flattenObject(value, separator);

            // Then add it to singular flat object, which each child key prefixed with top-level key and separator
            Object.entries(flattenedChild).forEach(([childKey, childValue]) => {
                flat[`${key}${separator}${childKey}`] = childValue;
            });
        } else {
            // Primitive, function, or null
            flat[key] = value;
        }
    });

    return flat;
}

/**
 * Flattens array into an object where their key is their index.
 * Recursively flattens child objects/arrays.
 *
 * E.g. `["aaa", { "key1": "bbb", key2: "ccc" }]` results in `{ "0": "aaa", "1.key1": "bbb", "1.key2": "ccc" }`
 */
export function flattenArray(array: unknown[], separator: string = '.') {
    const flat: Record<string, unknown> = {};

    array.forEach((item, index) => {
        const indexString = index.toString();

        if (item && typeof item === 'object') {
            const flattenedChild = Array.isArray(item) ? flattenArray(item, separator) : flattenObject(item, separator);

            // Then add it to singular flat object, which each child key prefixed with top-level key and separator
            Object.entries(flattenedChild).forEach(([childKey, childValue]) => {
                flat[`${indexString}${separator}${childKey}`] = childValue;
            });
        } else {
            // Primitive, function, or null
            flat[indexString] = item;
        }
    });

    return flat;
}
